import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import DcPage from "@/components/landing/dcv2/DcPage";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { authApi } from "@/services/endpoints/auth";

type VerifyState = "verifying" | "verified" | "failed";

// Button labels on the embedded apply wizard's "Check your email" step
// (employee-management-system `src/features/job-apply/AuthStep.tsx`). Keep them
// identical so the applicant finds the button this page names.
const APPLY_CONTINUE_LABEL = "I've verified my email — continue";
const APPLY_RESEND_LABEL = "Resend the email";

/**
 * Note for an applicant who signed up inside a company's embedded job
 * application. The emailed link carries no marker of where the sign-up started,
 * and this tab cannot reach the client's frame (partitioned storage), so the
 * note is shown to everyone, below the page's own message (T101826001).
 */
function EmbeddedApplyNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-4 text-left text-sm text-muted-foreground">
      <p className="font-medium text-foreground">Applying for a job on a company&apos;s website?</p>
      <p className="mt-1">{children}</p>
    </div>
  );
}

/**
 * Landing page for the link in the sign-up verification email. The API builds
 * that link to this site's `/verify-email` when the sign-up came from here or
 * from a company's embedded job application (`verifyBaseUrl`, T100826055,
 * T101826001); the page posts the token and sends the user on to sign in, or
 * back to the application tab.
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  // The portal sign-in prefills this email so the candidate only types the
  // password (T100826078).
  const signInPath = email ? `/auth?${new URLSearchParams({ email }).toString()}` : "/auth";

  const [state, setState] = useState<VerifyState>(token ? "verifying" : "failed");
  const [error, setError] = useState(
    token ? "" : "This verification link is invalid or incomplete."
  );
  // StrictMode runs effects twice in development; a token verifies only once,
  // so the second POST would report a spurious failure.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    authApi
      .verifyEmail(token)
      .then(() => setState("verified"))
      .catch((err: unknown) => {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          "We could not verify your email. The link may have expired or already been used.";
        setError(message);
        setState("failed");
      });
  }, [token]);

  return (
    <DcPage hideFooter>
      <div className="flex min-h-[calc(100vh-72px)] items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="p-6 sm:p-8 border border-border rounded-lg bg-card text-center space-y-4">
            {state === "verifying" && (
              <>
                <Loader2 className="mx-auto h-14 w-14 animate-spin text-muted-foreground" />
                <h1 className="text-2xl font-semibold">Verifying your email</h1>
                <p className="text-sm text-muted-foreground">This takes a moment.</p>
              </>
            )}

            {state === "verified" && (
              <>
                <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
                <h1 className="text-2xl font-semibold">Email verified</h1>
                <p className="text-sm text-muted-foreground">
                  {email ? (
                    <>
                      <span className="font-semibold text-foreground">{email}</span> is verified.
                    </>
                  ) : (
                    "Your email is verified."
                  )}{" "}
                  You can now sign in.
                </p>
                <Button className="w-full" onClick={() => navigate(signInPath)}>
                  Go to sign in
                </Button>
                <EmbeddedApplyNote>
                  Go back to the tab with your application and select{" "}
                  <span className="font-medium text-foreground">
                    &ldquo;{APPLY_CONTINUE_LABEL}&rdquo;
                  </span>{" "}
                  to finish applying there. You can close this tab.
                </EmbeddedApplyNote>
              </>
            )}

            {state === "failed" && (
              <>
                <XCircle className="mx-auto h-14 w-14 text-destructive" />
                <h1 className="text-2xl font-semibold">Verification failed</h1>
                <p className="text-sm text-muted-foreground">{error}</p>
                <Button className="w-full" onClick={() => navigate(signInPath)}>
                  Back to sign in
                </Button>
                <EmbeddedApplyNote>
                  Go back to the tab with your application. If you already verified this email,
                  select{" "}
                  <span className="font-medium text-foreground">
                    &ldquo;{APPLY_CONTINUE_LABEL}&rdquo;
                  </span>
                  ; otherwise select{" "}
                  <span className="font-medium text-foreground">
                    &ldquo;{APPLY_RESEND_LABEL}&rdquo;
                  </span>{" "}
                  there to get a new link.
                </EmbeddedApplyNote>
              </>
            )}
          </div>
        </div>
      </div>
    </DcPage>
  );
}
